import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import DropDownColor from "components/uiComponents/DropDownColor";
import UniversalButton from "components/uiComponents/UniversalButton";
import DropDownFolder from "components/uiComponents/DropDownFolder";

import { createCustomColumnsDto, showToast } from "utils/helpers";
import { getCollectionsForFolder } from "utils/builder/IScheduleLogic";

import {
  collectionEdit,
  collectionSave,
  createCollectionFolder,
} from "store/actions/collections";

import "./SaveCollectionModal.scss";
import classNames from "classnames";
import { getTabs } from "store/selectors/tabsSettings";

const SaveCollectionModal = ({
  type = "create",
  folderInfo: { folder, color, description, title } = {
    folder: "My selection sets",
    title: "",
    description: "",
    color: "#4F80FF",
  },
  tableInfo,
  selected,
  closeCollectionModal,
}) => {
  const { t } = useTranslation();

  const collection = useMemo(
    () => ({
      folder,
      collectionName: title,
      description,
      color,
    }),
    [color, description, folder, title]
  );

  const dispatch = useDispatch();

  const { templateSettings, collectionsFolders, activeFolder } = useSelector(
    (state) => state.viewer
  );
  const schemesForTabs = useSelector(getTabs);

  const currentTabColumnSchemes = createCustomColumnsDto(
    schemesForTabs?.[tableInfo?.id]?.columnSchemes
  );

  const [infoAboutCollection, setInfoAboutCollection] = useState(collection);
  const [nameError, setNameError] = useState(null);

  const getSelectFolder = (folderName) =>
    setInfoParameter({ folder: folderName });

  const saveCollection = async () => {
    const elements = tableInfo.lineElements.flat(2);

    const isValid = await validateCollectionName();
    
    if (isValid) {
      try {
        await dispatch(
          collectionSave({
            ...infoAboutCollection,
            elements,
            properties:
              tableInfo?.collection?.properties || templateSettings.properties,
            groups: tableInfo?.collection?.groupBy || templateSettings.groups,
            id: tableInfo?.id,
            type: tableInfo?.type,
            customColumns: currentTabColumnSchemes,
            activeFolder,
            selected,
          })
        );

        showToast(true, t('selection_created'));
      } catch (error) {
        showToast(false, error);
      }

      closeCollectionModal();
    }
  };

  const editCollection = async () => {
    const isValid = await validateCollectionName();

    if (isValid) {
      try {
        await dispatch(
          collectionEdit({
            oldTitle: collection.collectionName,
            oldFolder: collection.folder,
            newTitle: infoAboutCollection.collectionName,
            newFolder: infoAboutCollection.folder,
            newDescription: infoAboutCollection.description,
            newColor: infoAboutCollection.color,
          })
        );
        showToast(true, t('selection_created_edited'));
      } catch (error) {
        showToast(false, error);
      }
      closeCollectionModal();
    }
  };

  const validateCollectionName = async () => {
    const collectionName = infoAboutCollection.collectionName;
    const folder = infoAboutCollection.folder;
    const collections = await getCollectionsForFolder(folder);

    const isExistName = collections.every((collection) => {
      return collection.title !== collectionName;
    });

    if (!collectionName) {
      setNameError(t('selection_set_name_cannot_be_empty'));
      return false;
    }
    if (!isExistName && type === "create") {
      setNameError(t('name_already_exists'));
      return false;
    }

    return true;
  };

  const addFolder = async (folderName) => {
    try {
      await dispatch(createCollectionFolder(folderName));
      setInfoParameter({ folder: folderName });
      showToast(true, t('selection_sets_folder_created'));
    } catch (error) {
      showToast(false, error);
    }
  };

  const setInfoParameter = (value) =>
    setInfoAboutCollection({ ...infoAboutCollection, ...value });

  return (
    <div className="save-collection">
      <div className="modal__header">{`${
        type === "create" ? t('save') : t('edit')
      } ${t('selection_set')}`}</div>

      <div className="save-collection__name">
        <label htmlFor="save-collection__name">{t('selection_set_name')}</label>

        <input
          id="save-collection__name"
          className={classNames({ "save-collection__input-error": nameError })}
          type="text"
          value={infoAboutCollection.collectionName}
          onInput={(event) =>
            setInfoParameter({ collectionName: event.target.value })
          }
        />

        {nameError ? (
          <div className="save-collection__error-text">{nameError}</div>
        ) : null}
      </div>

      <div className="save-collection__description">
        <label htmlFor="save-collection__description">
          {t('description')}{" "}
          <span className="save-collection__description_gray">({t('optional')})</span>
        </label>

        <textarea
          id="save-collection__description"
          rows="3"
          value={infoAboutCollection.description}
          onInput={(event) =>
            setInfoParameter({ description: event.target.value })
          }
        />
      </div>

      <div className="save-collection__folder">
        <DropDownFolder
          listTitle={infoAboutCollection.folder}
          list={collectionsFolders}
          getSelectFolder={getSelectFolder}
          addFolder={addFolder}
        />
      </div>

      <div className="save-collection__color">
        <DropDownColor
          submitClick={setInfoParameter}
          color={infoAboutCollection.color}
        />
      </div>

      <div className="save-collection__btns">
        <div className="save-collection__btns_btn">
          <UniversalButton
            type="button"
            label={t('cancel')}
            size="lg"
            labelColor="blue"
            labelHoverColor="blue"
            backgroundColor="white"
            backgroundHoverColor="blue-light"
            onClick={closeCollectionModal}
          />
        </div>

        <UniversalButton
          type="button"
          label={t('save')}
          size="lg"
          labelColor="white"
          labelHoverColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
          onClick={type === "create" ? saveCollection : editCollection}
        />
      </div>
    </div>
  );
};

export default SaveCollectionModal;
